import React, { useState, useEffect } from 'react';
import { GetServerSidePropsContext } from 'next';
import { useEffectOnce } from 'react-use';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { always, flatten, pick, values } from 'ramda';
import { FormattedMessage } from 'react-intl';
import useLogout from '@bigcommerce/storefront-data-hooks/use-logout';

import { ArtworksView } from '@components/PLP/artworksView';
import api from '@services/api';
import { ARTWORKS_LIMIT } from '@modules/plp/plp.constants';
import reportError from '@utils/reportError';
import { Artwork, Meta } from '@definitions/artworks.types';
import { getPreviewToken, ls, redirectOnError } from '@utils/helpers';
import { TradeRoute } from '@components/tradeRoute';
import {
  useChangeGridSize,
  useFetchArtworks,
  useSetCurrentPage,
  useSetInitialArtworks,
} from '@hooks/useArtworks/useArtworks';
import { PLP_VARIANT } from '@modules/plp';
import { ArtworksGrid } from '@components/PLP/artworksGrid';
import { DROPDOWN_TYPES, FILTER_BUTTON_TEST_ID } from '@components/PLP/artworksView/artworksView.component';
import { useFilters } from '@hooks/useFilters';
import { useHeaderMobileMenu, useHeaderSublevel } from '@hooks/useUi/useUi';
import { FILTER_ORDER } from '@components/headerFilter/headerFilter.constants';
import { renderWhenTrue } from '@utils/rendering';
import { USER_WAITLISTED_ITEMS } from '@components/waitlistButton/waitlistButton.constants';
import { HEADER_TOP, SUBLEVEL } from '@components/header/header.constants';
import { eUserVariants } from '@components/tradeRoute/tradeRoute.component';
import { Header } from '@components/header';
import { MAIN_ANIMATION, ROUTE } from '@utils/constants';
import { Grouping as GroupingType } from '@definitions/header.types';
import {
  Container,
  MenuOption,
  MotionContainer,
  TradeModal,
  Content,
  Title,
  Controls,
  Button,
  DesktopGroupingsContainer,
  GroupingsList,
  Grouping,
  ControlsContainer,
  MobileGroupingsButton,
  TradeProgram,
} from '@components/PLP/artworksView/artworksView.styles';
import { HeaderFilter } from '@components/headerFilter';
import { GridSwitcher } from '@components/PLP/gridSwitcher';
import { FocusTrap } from '@components/common';
import { useGroupings } from '@hooks/useHeader';
import { Seo } from '@definitions/common.types';

const TRADE_PROGRAM_LANDING =
  'https://www.platformart.com/features/platform-trade-program-art-advisors-interior-designers';

export const getServerSideProps = async ({ req, query }: GetServerSidePropsContext) => {
  const { page = 1, ...restQuery } = query;

  try {
    const token = getPreviewToken(req.headers.cookie);
    const { price, inputPrice, ...restParams } = restQuery as { price: string[] | string; inputPrice: string };
    const newParams: { price?: string | string[] } = { ...(restParams || {}), price };
    if (inputPrice) {
      if (price) {
        newParams.price = typeof price === 'string' ? [price, inputPrice] : [...price, inputPrice];
      } else {
        newParams.price = inputPrice;
      }
    }

    const params = {
      limit: ARTWORKS_LIMIT * +page,
      offset: INITIAL_OFFSET,
      ...newParams,
    };

    const { data } = await api.get('/trade', {
      params,
      headers: { cookie: req.headers.cookie || '', authorization: token },
    });

    return {
      props: {
        initialArtworks: data.results,
        meta: { count: data.count, hasNext: !!data.next },
      },
    };
  } catch (e) {
    reportError(e);

    return redirectOnError(e);
  }
};

const INITIAL_OFFSET = 0;

interface IndexProps {
  initialArtworks: Artwork[];
  meta: Meta;
}

const Index = ({ meta, initialArtworks }: IndexProps) => {
  const { artworks, setInitialArtworks } = useSetInitialArtworks(PLP_VARIANT.TRADE);
  const { fetchArtworks, meta: tradeMeta, loading: tradeArtworksLoading } = useFetchArtworks(PLP_VARIANT.TRADE);
  const { currentPage } = useSetCurrentPage(PLP_VARIANT.TRADE);

  const { groupings, fetchGroupings } = useGroupings();
  const [dropdownOpen, setDropdownOpen] = useState<DROPDOWN_TYPES | null>(null);
  const [filtersCount, setFiltersCount] = useState(0);
  const { gridSize, changeGridSize } = useChangeGridSize(PLP_VARIANT.TRADE);
  const router = useRouter();
  const logout = useLogout();
  const { filterOptions, fetchTradeFilterOptions } = useFilters('');
  const { setHeaderMobileMenuOpen } = useHeaderMobileMenu();
  const { openHeaderSublevel } = useHeaderSublevel();
  const { groupingTitle } = getGroupingIntro();
  const groupingSlug = router.query.grouping as string;
  const isFilterOpen = dropdownOpen === DROPDOWN_TYPES.FILTER;
  const isFavoriteOpen = dropdownOpen === DROPDOWN_TYPES.FAVOURITE;
  let updatedGroupings;

  useEffectOnce(() => {
    setInitialArtworks({ artworks: initialArtworks, meta });
  });

  useEffect(() => {
    setFiltersCount(flatten(values(pick([...FILTER_ORDER, 'inputPrice'], router.query))).length);
  }, [router.query]);

  function getGroupingIntro() {
    const currentItem = groupings?.filter((i) => i.slug === groupingSlug)[0];
    return {
      groupingDescription: currentItem?.description || '',
      groupingTitle: currentItem?.name,
    };
  }

  const handlePageAvailable = async () => {
    const { meta: newMeta, artworks: newArtworks } = await fetchArtworks({ page: 1 });
    setInitialArtworks({ artworks: newArtworks, meta: newMeta });
  };

  const handleOnLoginClick = async () => {
    try {
      await logout();
      ls.remove(USER_WAITLISTED_ITEMS);
      openHeaderSublevel({ sublevel: SUBLEVEL.ACCOUNT, preventRedirectAfterLogin: true });
    } catch (e) {
      reportError(e);
    }
  };

  const toggleDropdown = (type: DROPDOWN_TYPES) => () =>
    type === dropdownOpen ? setDropdownOpen(null) : setDropdownOpen(type);

  const renderFiltersCount = renderWhenTrue(always(`: ${filtersCount}`));

  const renderFocusTrap = renderWhenTrue(
    always(<FocusTrap onFocus={() => setDropdownOpen(DROPDOWN_TYPES.FAVOURITE)} />)
  );

  const renderMobileGroupingsButton = renderWhenTrue(
    always(<MobileGroupingsButton onClick={() => setHeaderMobileMenuOpen(true)}>{groupingTitle}</MobileGroupingsButton>)
  );

  const renderTradeProgramLink = (idx: number) => (
    <a
      onClick={() => {
        window.history.pushState({}, '', ROUTE.TRADE);
      }}
      key={idx}
      href={ROUTE.TRADE}
    >
      <TradeProgram isActive={router.pathname === ROUTE.TRADE}>
        <FormattedMessage id="artworksView.tradeProgram" defaultMessage="Trade Program" />
      </TradeProgram>
    </a>
  );

  const renderGroupingLink = (grouping: GroupingType, idx: number) => (
    <NextLink
      key={idx}
      href={`${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/[grouping]`}
      as={`${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/${grouping.slug}`}
      passHref
    >
      <Grouping isActive={router.asPath.includes(`${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/${grouping.slug}`)}>
        {grouping.name}
      </Grouping>
    </NextLink>
  );

  const renderSecNavBarLink = (grouping: GroupingType, idx: number) => {
    return grouping.slug == 'trade' ? renderTradeProgramLink(idx) : renderGroupingLink(grouping, idx);
  };

  const allArtwork = {
    name: 'All Artworks',
    description: '',
    default: false,
    slug: 'artworks',
    seoPageTitle: '',
    seoDescription: '',
    seoTags: [],
    artistDesignedProductsLayout: false,
  };

  if (groupings && groupings.length > 0) {
    updatedGroupings = [allArtwork, ...groupings];
  } else {
    updatedGroupings = [allArtwork];
  }

  const renderGroupings = renderWhenTrue(
    always(
      <GroupingsList isHidden={!updatedGroupings || updatedGroupings.length === 0}>
        {updatedGroupings?.map((grouping, idx) => (
          <Grouping
            key={idx}
            isActive={
              grouping.name === 'All Artworks'
                ? router.asPath === ROUTE.ARTWORKS
                : router.asPath === `${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/${grouping.slug}`
            }
          >
            {grouping.name === 'All Artworks' ? (
              <a href={`${ROUTE.ARTWORKS}`}>{grouping.name}</a>
            ) : (
              <a href={`${ROUTE.ARTWORKS}${ROUTE.GROUPINGS}/${grouping.slug}`}>{grouping.name}</a>
            )}
          </Grouping>
        ))}
      </GroupingsList>
    )
  );

  const renderCustomHeader = () => (
    <Header
      headerTop={HEADER_TOP.REGULAR}
      childrenSize={51}
      isChildrenOpen={isFilterOpen}
      handleChildrenClose={() => setDropdownOpen(null)}
    >
      <MotionContainer
        initial={{ opacity: 0, y: -44 }}
        animate={{ opacity: 1, y: 0, transition: { duration: MAIN_ANIMATION.DURATION } }}
        exit={{ opacity: 0, y: -44, transition: { duration: MAIN_ANIMATION.DURATION } }}
      >
        <Container>
          <DesktopGroupingsContainer>{renderGroupings(!!groupings)}</DesktopGroupingsContainer>

          {renderMobileGroupingsButton(router.pathname !== ROUTE.ARTWORKS)}

          <ControlsContainer isFilterSelected={!!filtersCount}>
            <GridSwitcher gridSize={gridSize} changeGridSize={changeGridSize} />

            <MenuOption onClick={toggleDropdown(DROPDOWN_TYPES.FILTER)} data-testid={FILTER_BUTTON_TEST_ID}>
              <FormattedMessage id="artworksView.filterMenu" defaultMessage="Filter" />
              {renderFiltersCount(!!filtersCount)}
            </MenuOption>
          </ControlsContainer>
        </Container>
        {renderFocusTrap(isFavoriteOpen)}
        <HeaderFilter
          groupingSlug={groupingSlug}
          options={filterOptions}
          open={isFilterOpen}
          onClose={() => setDropdownOpen(null)}
        />
      </MotionContainer>
    </Header>
  );

  const renderRegularMemberMessage = renderWhenTrue(
    always(<Title>Currently you&apos;re logged in as a regular member. Please log in as a trade member.</Title>)
  );

  const renderTradeMemberModal = (userVariant: eUserVariants) =>
    renderWhenTrue(() => (
      <TradeModal>
        <Content>
          <Title>This page is only available for Platform trade members.</Title>
          {renderRegularMemberMessage(userVariant === eUserVariants.REGULAR)}
          <Controls>
            <Button onClick={() => router.push(TRADE_PROGRAM_LANDING)}>Learn more</Button>
            <Button onClick={() => handleOnLoginClick()}>Log in</Button>
          </Controls>
        </Content>
      </TradeModal>
    ));

  let seoImageUrl: Seo | null;
  if (artworks.length > 0) {
    seoImageUrl = {
      tags: [],
      title: '',
      noIndex: false,
      noFollow: false,
      image: {
        url: artworks[0].images[0].urlThumbnail,
        alt: artworks[0].images[0].imageFile,
      },
    };
  }

  return (
    <TradeRoute onPageAvailable={() => handlePageAvailable()}>
      {(isPageAvailable, userVariant, loadingCustomer) => (
        <ArtworksView
          fetchFiltersFn={() => fetchTradeFilterOptions()}
          customHeader={() => renderCustomHeader()}
          seoImageUrl={seoImageUrl}
        >
          {renderTradeMemberModal(userVariant)(!isPageAvailable && !loadingCustomer)}
          <ArtworksGrid
            context={{
              meta: tradeMeta,
              loading: tradeArtworksLoading,
              fetchArtworks: fetchArtworks,
              currentPage: currentPage,
            }}
            plp={PLP_VARIANT.TRADE}
            artworks={artworks}
            gridSize={gridSize}
          />
        </ArtworksView>
      )}
    </TradeRoute>
  );
};

export default Index;
